import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { MolecularPathSetup as MolecularPathSetupType, TestName } from '../../types/diagnosis';
import toast from 'react-hot-toast';

export default function MolecularPathSetup() {
  const [setups, setSetups] = useState<MolecularPathSetupType[]>([]);
  const [tests, setTests] = useState<TestName[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSetup, setEditingSetup] = useState<MolecularPathSetupType | null>(null);
  const [formData, setFormData] = useState({
    test_id: '',
    test_code: '',
    methodology: '',
    sample_type: '',
    turnaround_time: '',
  });

  useEffect(() => {
    loadTests();
    loadSetups();
  }, []);

  const loadTests = async () => {
    try {
      const { data, error } = await supabase
        .from('test_names')
        .select('*')
        .order('test_name', { ascending: true });

      if (error) throw error;
      setTests(data || []);
    } catch (error: any) {
      console.error('Error loading tests:', error);
      toast.error('Failed to load tests');
    }
  };

  const loadSetups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('molecular_path_setup')
        .select(`
          *,
          test:test_names(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSetups(data || []);
    } catch (error: any) {
      console.error('Error loading setups:', error);
      toast.error('Failed to load molecular pathology setups');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingSetup) {
        const { error } = await supabase
          .from('molecular_path_setup')
          .update(formData)
          .eq('id', editingSetup.id);

        if (error) throw error;
        toast.success('Setup updated successfully');
      } else {
        const { error } = await supabase
          .from('molecular_path_setup')
          .insert([formData]);

        if (error) throw error;
        toast.success('Setup added successfully');
      }

      setShowForm(false);
      setEditingSetup(null);
      setFormData({
        test_id: '',
        test_code: '',
        methodology: '',
        sample_type: '',
        turnaround_time: '',
      });
      loadSetups();
    } catch (error: any) {
      console.error('Error saving setup:', error);
      toast.error(error.message || 'Failed to save setup');
    }
  };

  const handleEdit = (setup: MolecularPathSetupType) => {
    setEditingSetup(setup);
    setFormData({
      test_id: setup.test_id,
      test_code: setup.test_code || '',
      methodology: setup.methodology || '',
      sample_type: setup.sample_type || '',
      turnaround_time: setup.turnaround_time || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this setup?')) return;

    try {
      const { error } = await supabase
        .from('molecular_path_setup')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Setup deleted successfully');
      loadSetups();
    } catch (error: any) {
      console.error('Error deleting setup:', error);
      toast.error('Failed to delete setup');
    }
  };

  const filteredSetups = setups.filter((setup) =>
    setup.test?.test_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    setup.test_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Molecular Pathology Setup</h1>
        <p className="text-gray-600">Configure molecular pathology test parameters</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search setups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingSetup(null);
              setFormData({
                test_id: '',
                test_code: '',
                methodology: '',
                sample_type: '',
                turnaround_time: '',
              });
            }}
            className="ml-4 flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Setup
          </button>
        </div>

        {showForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">
              {editingSetup ? 'Edit Setup' : 'Add New Setup'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.test_id}
                    onChange={(e) => setFormData({ ...formData, test_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">--Select Test--</option>
                    {tests.map((test) => (
                      <option key={test.id} value={test.id}>
                        {test.test_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Code
                  </label>
                  <input
                    type="text"
                    value={formData.test_code}
                    onChange={(e) => setFormData({ ...formData, test_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Methodology
                  </label>
                  <input
                    type="text"
                    value={formData.methodology}
                    onChange={(e) => setFormData({ ...formData, methodology: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., PCR, NGS, FISH"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sample Type
                  </label>
                  <input
                    type="text"
                    value={formData.sample_type}
                    onChange={(e) => setFormData({ ...formData, sample_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Blood, Tissue, Saliva"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Turnaround Time
                  </label>
                  <input
                    type="text"
                    value={formData.turnaround_time}
                    onChange={(e) => setFormData({ ...formData, turnaround_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., 3-5 days, 1 week"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingSetup(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingSetup ? 'Update' : 'Add'} Setup
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Methodology
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sample Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Turnaround Time
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredSetups.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No setups found
                  </td>
                </tr>
              ) : (
                filteredSetups.map((setup, index) => (
                  <tr key={setup.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {setup.test?.test_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {setup.test_code || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {setup.methodology || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {setup.sample_type || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {setup.turnaround_time || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(setup)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(setup.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
